import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Logo } from '../components/Logo';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="font-heading font-bold text-lg">Terms & Conditions</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Logo className="h-10 mb-6" />
          
          <Card className="glass-card p-6 space-y-6">
            <section>
              <h2 className="font-heading font-bold text-lg mb-3">1. Acceptance of Terms</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                By accessing and using HealthLink360, you accept and agree to be bound by the terms and 
                conditions of this agreement. HealthLink360 is a wellness platform providing general 
                health and lifestyle guidance. Our services are not intended to diagnose, treat, cure, 
                or prevent any disease or health condition.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">2. Service Description</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                HealthLink360 provides AI-powered wellness coaching, habit tracking, and personalized 
                health insights through your Digital Twin Score. Our platform uses HIPAA-adjacent 
                standards to maintain secure access to your health information. We utilize facial 
                scanning technology (rPPG) and wearable device integrations to gather biometric data 
                for wellness insights.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">3. Medical Disclaimer</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                HealthLink360 is NOT a medical device and does NOT provide medical advice. The Digital 
                Twin Score and all recommendations are for informational and wellness purposes only. 
                Always consult with a qualified healthcare provider before making any changes to your 
                health routine, especially if you have existing medical conditions. In case of emergency, 
                call 911 or your local emergency services immediately.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">4. User Responsibilities</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials. 
                You agree to provide accurate information during registration and keep your profile 
                updated. You must be at least 13 years of age to use HealthLink360. Users under 18 
                should have parental or guardian consent.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">5. Subscription and Billing</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                HealthLink360 offers a 7-day free trial. After the trial period, continued access 
                requires a paid subscription. You can cancel your subscription at any time. Refunds 
                are processed according to our refund policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">6. Intellectual Property</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All content, features, and functionality of HealthLink360 are owned by HealthLink360 
                and are protected by international copyright, trademark, and other intellectual 
                property laws.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">7. Limitation of Liability</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                HealthLink360 shall not be liable for any indirect, incidental, special, consequential, 
                or punitive damages resulting from your use or inability to use the service. Our total 
                liability shall not exceed the amount paid by you for the service in the 12 months 
                preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">8. Changes to Terms</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of 
                significant changes via email or in-app notification. Continued use of the service 
                after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">9. Contact Information</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For questions about these Terms & Conditions, please contact us at{' '}
                <a href="mailto:hello@healthlink360.ai" className="text-primary hover:underline">
                  hello@healthlink360.ai
                </a>
              </p>
            </section>

            <p className="text-xs text-muted-foreground pt-4 border-t border-border">
              Last updated: December 2025
            </p>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
